import { ReactNode, createContext, useContext, useMemo } from 'react';

import { FieldInputProps, useField } from 'react-final-form';

import {
  RffControlsProvider,
  useControlsGroupState,
} from '@cast/shared-rff-ui';

type OverrideGroupContextType = {
  isOverriddenInput: FieldInputProps<boolean>;
  groupName: string;
  name: string;
  canOverride: boolean;
};

const OverrideGroupContext = createContext<OverrideGroupContextType | null>(
  null
);

type OverrideGroupProviderProps = {
  name: string;
  children: ReactNode;
  canOverride?: boolean;
};

export const OverrideGroupProvider = ({
  children,
  name,
  canOverride = false,
}: OverrideGroupProviderProps) => {
  const {
    name: groupName = name, // default to name if not wrapped to form group
  } = useControlsGroupState({
    name,
  });

  // button `override` or `revert to policy`,
  // isOverriddenField is the field that holds the value of the override
  const isOverriddenInput = useField<boolean>(
    `${groupName}.isOverridden`
  ).input;

  return (
    <OverrideGroupContext.Provider
      value={useMemo(
        () =>
          ({
            name, // e.g. `antiAffinity`
            groupName, // e.g. `verticalRightsizing.policy.antiAffinity`
            isOverriddenInput,
            canOverride,
          } satisfies OverrideGroupContextType),
        [name, groupName, isOverriddenInput, canOverride]
      )}
    >
      <RffControlsProvider
        readOnly={!canOverride || !isOverriddenInput.value}
        namePrefix={name}
      >
        {children}
      </RffControlsProvider>
    </OverrideGroupContext.Provider>
  );
};

export const useOverrideGroup = () => {
  const context = useContext(OverrideGroupContext);

  if (context === null) {
    throw new Error(
      'useOverrideGroup must be used within a OverrideGroupProvider'
    );
  }

  return context;
};
